import { gsap } from 'gsap';

const initializeTabGroup = (group) => {
  const tabs = [...group.querySelectorAll('[data-tab-group-trigger]')];
  const accordionTriggers = [
    ...group.querySelectorAll('[data-tab-group-accordion-trigger]'),
  ];
  const panels = [...group.querySelectorAll('[data-tab-group-panel]')];

  if (!tabs.length || tabs.length !== panels.length) return;

  const desktop = window.matchMedia('(min-width: 1024px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeIndex = -1;

  const stopAnimations = () => {
    gsap.killTweensOf(panels);
    panels.forEach((panel, itemIndex) => {
      gsap.set(panel, { clearProps: 'opacity,transform' });
      panel.hidden = itemIndex !== activeIndex;
    });
  };

  const setActiveItem = (index, { focus = false } = {}) => {
    if (index === activeIndex) {
      if (focus) tabs[index]?.focus();
      return;
    }

    stopAnimations();
    const previousIndex = activeIndex;
    activeIndex = index;

    tabs.forEach((tab, itemIndex) => {
      const isActive = itemIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    accordionTriggers.forEach((trigger, itemIndex) => {
      trigger.setAttribute('aria-expanded', String(itemIndex === index));
    });

    panels.forEach((panel, itemIndex) => {
      const isActive = itemIndex === index;
      panel.inert = !isActive;
      panel.setAttribute('aria-hidden', String(!isActive));
      if (isActive) panel.hidden = false;
    });

    if (previousIndex >= 0 && (!desktop.matches || reducedMotion.matches)) {
      panels[previousIndex].hidden = true;
    }

    if (previousIndex >= 0 && !reducedMotion.matches) {
      gsap.fromTo(
        panels[index],
        {
          opacity: 0,
          y: '1rem',
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.3,
          ease: 'power1.out',
          onComplete: () =>
            gsap.set(panels[index], { clearProps: 'opacity,transform' }),
        },
      );

      if (desktop.matches) {
        const outgoingPanel = panels[previousIndex];
        gsap.fromTo(
          outgoingPanel,
          {
            opacity: 1,
            y: 0,
          },
          {
            opacity: 0,
            y: '-1rem',
            duration: 0.3,
            ease: 'power1.out',
            onComplete: () => {
              outgoingPanel.hidden = true;
              gsap.set(outgoingPanel, { clearProps: 'opacity,transform' });
            },
          },
        );
      }
    }

    if (focus) tabs[index]?.focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => setActiveItem(index));
    tab.addEventListener('keydown', (event) => {
      let nextIndex;

      if (['ArrowDown', 'ArrowRight'].includes(event.key)) {
        nextIndex = (index + 1) % tabs.length;
      } else if (['ArrowUp', 'ArrowLeft'].includes(event.key)) {
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      setActiveItem(nextIndex, { focus: true });
    });
  });

  accordionTriggers.forEach((trigger, index) => {
    trigger.addEventListener('click', () => setActiveItem(index));
  });

  setActiveItem(0);
  group.classList.add('is-enhanced');
  desktop.addEventListener('change', stopAnimations);
  reducedMotion.addEventListener('change', stopAnimations);
};

export const initializeTabGroups = () => {
  document.querySelectorAll('[data-tab-group]').forEach(initializeTabGroup);
};
